import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CategoryDropdown from './components/CategoryDropdown';
import CategoryGrid from './components/CategoryGrid';
import FaqSection from './components/FaqSection';
import { heroImage, mainSiteUrl } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { usePageLanguage, useLocalizedPath } from '@/hooks/useLanguage';
import { useSeo } from '@/hooks/useSeo';
import JsonLd from '@/components/base/JsonLd';
import { SITE_NAME, FAVICON_URL, absoluteUrl } from '@/lib/site';

const featureImages = [
  'https://readdy.ai/api/search-image?query=A%20modern%20minimalist%20spreadsheet%20interface%20with%20colorful%20organized%20data%20rows%20and%20columns%20displayed%20on%20a%20sleek%20laptop%20screen%2C%20soft%20natural%20desk%20lighting%2C%20clean%20workspace%20with%20a%20small%20plant%20nearby%2C%20bright%20airy%20tech%20photography%2C%20organized%20and%20productive%20mood%2C%20high%20detail&width=640&height=400&seq=feature-spreadsheet&orientation=landscape',
  'https://readdy.ai/api/search-image?query=A%20close-up%20of%20five%20golden%20star%20icons%20floating%20above%20an%20open%20notebook%20with%20a%20pen%20on%20a%20warm%20wooden%20desk%2C%20soft%20warm%20lighting%20with%20subtle%20bokeh%2C%20editorial%20review%20and%20rating%20concept%20photography%2C%20trustworthy%20and%20inviting%20composition&width=640&height=400&seq=feature-review&orientation=landscape',
  'https://readdy.ai/api/search-image?query=A%20brown%20cardboard%20shipping%20box%20sealed%20with%20packing%20tape%20and%20a%20shipping%20label%20on%20a%20light%20neutral%20studio%20background%2C%20soft%20even%20studio%20lighting%2C%20minimalist%20logistics%20and%20delivery%20concept%20photography%2C%20clean%20and%20efficient%20composition&width=640&height=400&seq=feature-shipping&orientation=landscape',
  'https://readdy.ai/api/search-image?query=A%20red%20gift%20tag%20with%20a%20percentage%20discount%20symbol%20and%20a%20pair%20of%20scissors%20next%20to%20a%20neat%20stack%20of%20folded%20clothing%2C%20bright%20cheerful%20studio%20lighting%2C%20ecommerce%20sale%20and%20coupon%20concept%20photography%2C%20playful%20and%20promotional%20mood&width=640&height=400&seq=feature-coupon&orientation=landscape',
  'https://readdy.ai/api/search-image?query=A%20well%20organized%20warehouse%20with%20tall%20metal%20shelving%20racks%20filled%20with%20neatly%20stacked%20cardboard%20boxes%2C%20bright%20industrial%20lighting%2C%20clean%20modern%20logistics%20facility%20interior%2C%20professional%20storage%20and%20fulfillment%20photography%2C%20spacious%20and%20orderly&width=640&height=400&seq=feature-warehouse&orientation=landscape',
  'https://readdy.ai/api/search-image?query=A%20green%20shield%20with%20a%20white%20check%20mark%20icon%20rendered%20in%20soft%20three%20dimensional%20style%20on%20a%20smooth%20pastel%20background%2C%20gentle%20studio%20lighting%2C%20trust%20safety%20and%20security%20concept%20photography%2C%20reassuring%20and%20clean%20minimal%20design&width=640&height=400&seq=feature-legit&orientation=landscape',
];

const featureSlugs = [
  '/usfans-spreadsheet-guide',
  '/usfans-review',
  '/usfans-shipping-cost',
  '/usfans-coupon',
  '/usfans-warehouse',
  '/usfans-legit',
];

const homeJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      name: SITE_NAME,
      url: absoluteUrl('/'),
      logo: { '@type': 'ImageObject', url: FAVICON_URL },
    },
    {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: absoluteUrl('/'),
      inLanguage: ['en', 'pl', 'es', 'fr'],
    },
  ],
};

export default function Home() {
  const { t, i18n } = useTranslation();
  usePageLanguage();
  useSeo('');
  const localize = useLocalizedPath();

  useEffect(() => {
    document.title = t('hero.h1');
  }, [t, i18n.language]);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={homeJsonLd} />
      <Navbar />

      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 overflow-hidden">
          <img src={heroImage} alt="USFans Spreadsheet" className="h-full w-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        </div>

        <div className="relative z-10 mx-auto flex min-h-[520px] w-full max-w-7xl flex-col items-center justify-center px-4 py-20 text-center md:min-h-[620px] md:px-6 md:py-28">
          <span className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-4 py-1.5 text-xs font-medium text-background-50 backdrop-blur-sm md:text-sm">
            <i className="ri-shield-star-line w-4 h-4 flex items-center justify-center"></i>
            {t('hero.badge')}
          </span>

          <h1 className="mt-6 max-w-4xl font-heading text-3xl font-semibold leading-tight text-background-50 md:text-5xl md:leading-tight">
            {t('hero.h1')}
          </h1>

          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-100 md:text-lg">
            {t('hero.sub')}
          </p>

          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:gap-4">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap md:text-base"
            >
              {t('hero.ctaPrimary')}
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
            </a>
            <Link
              to={localize('/usfans-spreadsheet-guide')}
              className="inline-flex items-center gap-2 rounded-full border border-background-50/40 bg-background-50/10 px-7 py-3 text-sm font-semibold text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20 whitespace-nowrap md:text-base"
            >
              {t('hero.ctaSecondary')}
            </Link>
          </div>

          <p className="mt-8 text-xs text-background-200 md:text-sm">{t('hero.trust')}</p>
        </div>
      </section>

      {/* CATEGORIES */}
      <section id="categories" className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-semibold text-foreground-950 md:text-4xl">
            {t('categories.title')}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">
            {t('categories.sub')}
          </p>
        </div>

        <div className="mt-8 flex flex-col items-center">
          <CategoryDropdown />
          <p className="mt-3 text-xs text-foreground-500">{t('categories.hint')}</p>
        </div>

        <div className="mt-10">
          <CategoryGrid />
        </div>
      </section>

      {/* FEATURES */}
      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <div className="text-center">
            <h2 className="font-heading text-2xl font-semibold text-foreground-950 md:text-4xl">
              {t('features.title')}
            </h2>
            <p className="mt-3 text-sm text-foreground-600 md:text-base">{t('features.sub')}</p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {featureImages.map((image, i) => (
              <Link
                key={i}
                to={localize(featureSlugs[i])}
                className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all hover:-translate-y-1 hover:border-primary-200"
              >
                <div className="h-44 w-full overflow-hidden">
                  <img
                    src={image}
                    alt={t(`features.${i + 1}.title`)}
                    title={`${t(`features.${i + 1}.title`)} USFans`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-base font-semibold text-foreground-950 md:text-lg">
                    {t(`features.${i + 1}.title`)}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    {t(`features.${i + 1}.desc`)}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-600">
                    {t('guide.cta')}
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center transition-transform group-hover:translate-x-0.5"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PILLAR GUIDES */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-semibold text-foreground-950 md:text-4xl">
            {t('guides.title')}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">
            {t('guides.sub')}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {articles.map((a) => (
            <Link
              key={a.slug}
              to={localize(`/${a.slug}`)}
              className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-all hover:-translate-y-0.5 hover:border-primary-300"
            >
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-900">
                <i className="ri-article-line text-lg"></i>
              </span>
              <div className="min-w-0 flex-1">
                <span className="text-xs font-semibold text-primary-600">{t(`${a.keyPrefix}.category`)}</span>
                <p className="mt-0.5 truncate text-sm font-semibold text-foreground-900 group-hover:text-primary-600">
                  {t(`${a.keyPrefix}.shortTitle`)}
                </p>
              </div>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
            </Link>
          ))}
        </div>
      </section>

      {/* ABOUT / GUIDE CTA */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div>
            <h2 className="font-heading text-2xl font-semibold text-foreground-950 md:text-4xl">
              {t('about.title')}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              {t('about.p1')}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              {t('about.p2')}
            </p>
          </div>

          <div className="rounded-lg border border-background-200 bg-gradient-to-br from-primary-50 to-accent-50 p-8 md:p-10">
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
              <i className="ri-book-open-line text-xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
              {t('guide.title')}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">{t('guide.sub')}</p>
            <div className="mt-6">
              <Link
                to={localize('/usfans-spreadsheet-guide')}
                className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                {t('guide.cta')}
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
            <p className="mt-4 text-xs text-foreground-500">{t('guide.meta')}</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection />

      {/* MAIN CTA */}
      <section className="bg-gradient-to-br from-primary-500 to-primary-600">
        <div className="mx-auto max-w-7xl px-4 py-16 text-center md:px-6 md:py-24">
          <h2 className="font-heading text-2xl font-semibold text-background-50 md:text-4xl">
            {t('cta.title')}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm text-background-100 md:text-base">
            {t('cta.sub')}
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 rounded-full bg-background-50 px-7 py-3 text-sm font-semibold text-primary-600 transition-colors hover:bg-background-100 whitespace-nowrap md:text-base"
            >
              {t('cta.primary')}
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 rounded-full border border-background-50/40 bg-background-50/10 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/20 whitespace-nowrap md:text-base"
            >
              {t('cta.secondary')}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}